import { BaseBlock, ParsedBlock } from '../chat-data/types'

export type BlockOrCompositeBlock =
  | Exclude<ParsedBlock, { type: 'MessageToPiperAi' } | { type: 'SystemMessage' }>
  | ({ type: 'SystemMessage' } & Extract<ParsedBlock, { type: 'SystemMessage' }> & {
        payload: Extract<
          Extract<ParsedBlock, { type: 'SystemMessage' }>['payload'],
          { type: 'ConversationSessionExpired' } | { type: 'SystemConversationAssigned' }
        >
      })
  | ({ type: 'SystemUserResponded' } & BaseBlock)
  | ({
      type: 'SystemChatTransferred'
    } & BaseBlock)

/**
 * Batches neighbouring message blocks and adds FE only events based on BE event sequences
 *
 * FE: SystemUserResponded
 *
 * - SystemMessage (SystemConversationAssigned)
 * - LiveMessage
 *
 * Or
 *
 * - SystemMessage (SystemConversationReassigned)
 * - LiveMessage
 *
 * FE: SystemChatTransferred
 *
 * - LiveMessage
 * - SystemMessage (SystemConversationReassigned)
 */
export const getBlocksWithCompositeBlocks = (
  blocks: ParsedBlock[],
  guestId: string
): BlockOrCompositeBlock[] => {
  const result: BlockOrCompositeBlock[] = []
  let isPendingLiveMessage = false
  let isPendingConversationReassigned = false
  for (const block of blocks) {
    if (block.type === 'SystemMessage' && block.payload.type === 'ConversationSessionExpired') {
      isPendingLiveMessage = false
      isPendingConversationReassigned = false
    }
    if (
      block.type === 'SystemMessage' &&
      (block.payload.type === 'SystemConversationAssigned' ||
        block.payload.type === 'SystemConversationReassigned')
    ) {
      isPendingLiveMessage = true
    }
    if (block.type === 'LiveMessage' && block.author !== `${guestId}_guest`) {
      isPendingConversationReassigned = true
      if (isPendingLiveMessage) {
        isPendingLiveMessage = false
        result.push({
          ...block,
          id: `${block.id}-composite`,
          type: 'SystemUserResponded',
        })
      }
    }
    if (
      isPendingConversationReassigned &&
      block.type === 'SystemMessage' &&
      block.payload.type === 'SystemConversationReassigned'
    ) {
      isPendingConversationReassigned = false
      result.push({
        ...block,
        id: `${block.id}-composite`,
        type: 'SystemChatTransferred',
      })
    }
    const lastBlock = result[result.length - 1]
    if (
      (block.type === 'LiveMessage' ||
        block.type === 'Message' ||
        block.type === 'PiperAiMessage') &&
      block.type === lastBlock?.type &&
      block.author === lastBlock.author
    ) {
      lastBlock.parts = [...lastBlock.parts, ...block.parts]
      if (block.type === 'PiperAiMessage' && lastBlock.type === 'PiperAiMessage') {
        // If there's a new piper ai message it means that the previous message is complete and we should use the status from the new message
        lastBlock.isComplete = block.isComplete
      }
    } else if (
      // MessageToPiperAi is a different because it doesn't have parts so we're turning them into a LiveMessage instead
      block.type === 'MessageToPiperAi'
    ) {
      const { value, ...blockWithoutValue } = block
      if (lastBlock?.type === 'LiveMessage' && lastBlock.author === block.author) {
        lastBlock.parts = [
          ...lastBlock.parts,
          {
            type: 'Text',
            text: value.content,
          },
        ]
      } else {
        result.push({
          ...blockWithoutValue,
          type: 'LiveMessage',
          parts: [
            {
              type: 'Text',
              text: value.content,
            },
          ],
        })
      }
    } else if (
      block.type === 'SystemMessage' &&
      (block.payload.type === 'ConversationSessionExpired' ||
        block.payload.type === 'SystemConversationAssigned')
    ) {
      result.push(structuredClone({ ...block, payload: block.payload }))
    } else if (block.type !== 'SystemMessage') {
      result.push(structuredClone(block))
    }
  }
  return result
}
